/**
 * DECISIONS 166 at the table (68.33's cards; lane A's convoys, 69.4 step 5, core/convoys.ts): what a side's supply is
 * doing, read for its cards. The base: its stock of loads (of CONVOY.baseStock, never spent below CONVOY.baseMinimum), when
 * the next one comes, its supply trucks and the runs under way. A FOB: its crate's fill (of FOB.stock), the run bound for
 * it, and what refills it (the runs, or the link while the side has no trucks). A truck: its cargo (of CONVOY.load) and its
 * leg. Pure: reads `state.convoys`, the trucks and the crates; presentation only (nothing here moves the Match).
 */
import type { ConvoyRun, GameState, StructureState } from '../types';
import { CONVOY, FOB, PHYS_DT } from '../constants';
import { cargoOnly, drives, structureTeam } from './body';
import { convoysOn, linkRefills, cargoFull } from './convoys';

/** Where a truck is in its round: at home (loading, or loaded and waiting), out to a FOB, home from one, or parked elsewhere. */
export type TruckLeg = 'home' | 'out' | 'back' | 'parked';

export interface TruckCard {
  truckId: number;
  label: string;
  /** standing, or destroyed (its cargo lost with it) */
  down: boolean;
  cargo: { bandages: number; magazines: number };
  /** a full load (CONVOY.load) aboard */
  full: boolean;
  leg: TruckLeg;
  /** the FOB its run is for (out or back) */
  fobId?: number;
  /** its run's driver, by name */
  driver?: string;
}

export interface BaseSupply {
  /** loads in the base's stock, its most, and the floor a truck never takes it below */
  stock: number; max: number; minimum: number;
  /** seconds until the next load (null at its most) */
  nextS: number | null;
  trucks: TruckCard[];
  /** runs set out and not yet ended */
  runs: number;
}

export interface FobSupply {
  crate: { bandages: number; magazines: number };
  /** the crate's fill, the lower of its two shares of FOB.stock */
  fill: number;
  /** the run bound for it (its truck and driver), not yet handed over */
  run: { truckId: number; driver?: string } | null;
  /** what refills it: its side's runs, or the link's trickle (a side with no supply trucks in its stock) */
  by: 'runs' | 'link';
}

const isTruck = (s: StructureState): boolean => cargoOnly(s) && drives(s);
/** The run under way for a truck (set out, not ended), or undefined. */
const openRun = (state: GameState, truckId: number): ConvoyRun | undefined => (state.convoys?.runs ?? []).find(r => r.truckId === truckId && !r.end);
const nameOf = (state: GameState, unitId: number): string | undefined => state.players.find(p => p.id === unitId)?.name;

/** A supply truck's card (`team`'s own), or null when it is not one, or convoys are off. */
export function truckCard(state: GameState, team: number, truckId: number): TruckCard | null {
  if (!convoysOn(state.config)) return null;
  const s = state.structures.find(x => x.id === truckId);
  if (!s || !isTruck(s) || structureTeam(state, s) !== team) return null;
  const run = openRun(state, s.id), cargo = { bandages: s.cargo?.bandages ?? 0, magazines: s.cargo?.magazines ?? 0 };
  // (a run's legs by its marks: set out, handed over, home again — home, the driver waits for the next wave to go forward)
  const leg: TruckLeg = run && !run.home ? (run.delivered !== undefined ? 'back' : 'out') : s.atHome ? 'home' : 'parked';
  const driver = run ? nameOf(state, run.unitId) : undefined;
  return {
    truckId: s.id, label: `Supply truck ${s.id}`, down: s.health <= 0, cargo, full: cargoFull(s.cargo), leg,
    ...(run && leg !== 'home' ? { fobId: run.fobId } : {}), ...(driver && leg !== 'home' ? { driver } : {}),
  };
}

/** `team`'s base supply for its card, or null with convoys off. `tick`: the Match's (the stock grows on CONVOY.baseRefillS's beat). */
export function baseSupplyCard(state: GameState, team: number, tick: number): BaseSupply | null {
  if (!convoysOn(state.config) || !state.convoys) return null;
  const stock = state.convoys.stock[team] ?? 0, every = Math.round(CONVOY.baseRefillS / PHYS_DT);
  const trucks = state.structures.filter(s => isTruck(s) && s.health > 0 && structureTeam(state, s) === team).sort((a, b) => a.id - b.id)
    .map(s => truckCard(state, team, s.id)!);
  return {
    stock, max: CONVOY.baseStock, minimum: CONVOY.baseMinimum,
    nextS: stock >= CONVOY.baseStock ? null : Math.round((every - (tick % every)) * PHYS_DT),
    trucks, runs: state.convoys.runs.filter(r => r.team === team && !r.end && !r.home).length,
  };
}

/** A FOB's supply for its card (`team`'s own, standing, with a crate), or null with convoys off or none. */
export function fobSupplyCard(state: GameState, team: number, fobId: number): FobSupply | null {
  if (!convoysOn(state.config)) return null;
  const f = state.structures.find(x => x.id === fobId);
  if (!f || f.type !== 'fob' || !f.supply || structureTeam(state, f) !== team) return null;
  const crate = { bandages: f.supply.bandages, magazines: f.supply.magazines };
  const run = (state.convoys?.runs ?? []).find(r => r.team === team && r.fobId === f.id && !r.end && r.delivered === undefined);
  const driver = run ? nameOf(state, run.unitId) : undefined;
  return {
    crate, fill: Math.min(crate.bandages / FOB.stock.bandages, crate.magazines / FOB.stock.magazines),
    run: run ? { truckId: run.truckId, ...(driver ? { driver } : {}) } : null,
    by: linkRefills(state, team) ? 'link' : 'runs',
  };
}

/** `team`'s supply trucks standing, for the table to draw and pick (id, where). */
export function tableTrucks(state: GameState, team: number): StructureState[] {
  if (!convoysOn(state.config)) return [];
  return state.structures.filter(s => isTruck(s) && s.health > 0 && structureTeam(state, s) === team);
}
